import { omit } from 'lodash';
import {
  Datagrid,
  List,
  Create,
  Edit,
  SimpleForm,
  TextField,
  TextInput,
  Show,
  TabbedShowLayout,
  Tab,
  usePermissions,
  useRecordContext,
} from 'react-admin';
import { Box } from '@mui/material';
import { ArchivalToolbar } from '../components/ArchivalToolbar';
import {
  ArchivedSelect,
  AssignmentField,
  AssignmentSelect,
  CourseField,
  CourseIdSelect,
  CourseSelect,
  FullNameField,
  StudentGroupField,
  StudentGroupSelect,
} from '../components/selects';
import { useCourseIds } from '../utils/useCourseIds';
import { can, type Permissions, type Student } from '../types';
import { BiometricsTab } from './wearables';
import { KsbProfileTab } from './ksb-profile';

const StudentList = () => {
  const course_ids = useCourseIds();
  const { permissions } = usePermissions<Permissions>();

  return (
    <List
      filter={course_ids ? { course_id: course_ids } : undefined}
      exporter={false}
      sort={{ field: 'last_name', order: 'ASC' }}
      filters={[
        <CourseIdSelect key="course" alwaysOn />,
        <ArchivedSelect key="archived" />,
      ]}
      perPage={25}
    >
      <Datagrid
        rowClick={can(permissions, 'manage_users') ? 'edit' : 'show'}
      >
        <TextField source="first_name" />
        <TextField source="middle_initial" />
        <TextField source="last_name" />
        <CourseField />
      </Datagrid>
    </List>
  );
};

const StudentShow = () => (
  <Show>
    <TabbedShowLayout>
      <Tab label="Profile">
        <FullNameField />
        <CourseField />
        <StudentGroupField />
        <AssignmentField />
        <TextField label="Rank" source="attributes.rank" />
        <TextField label="Height" source="attributes.height" />
        <TextField label="Weight" source="attributes.weight" />
        <TextField label="AFTP Score" source="attributes.aftp" />
        <TextField label="Driver's License" source="attributes.drivers_license" />
        <TextField
          label="Weapons Qualification"
          source="attributes.weapons_qualification"
        />
      </Tab>
      <Tab label="KSB Profile">
        <KsbProfileTab />
      </Tab>
      <Tab label="Biometrics">
        <BiometricsTab />
      </Tab>
    </TabbedShowLayout>
  </Show>
);

const StudentCreate = () => (
  <Create>
    <StudentForm />
  </Create>
);

const StudentEdit = () => (
  <Edit mutationMode="pessimistic">
    <StudentForm />
  </Edit>
);

const StudentForm = () => {
  const record = useRecordContext<Student>();
  const attrs = record?.attributes
    ? omit(record.attributes, [
        'first_name',
        'middle_initial',
        'last_name',
        'full_name',
      ])
    : {};

  return (
    <SimpleForm toolbar={<ArchivalToolbar />}>
      <Box display="flex" gap={2} width="100%">
        <TextInput source="first_name" />
        <TextInput source="middle_initial" />
        <TextInput source="last_name" />
      </Box>
      <CourseSelect disabled />
      <StudentGroupSelect disabled />
      <AssignmentSelect />
      {Object.keys(attrs).map((key) => (
        <TextInput
          key={key}
          fullWidth
          label={`${key[0].toUpperCase()}${key.slice(1).replace(/_/g, ' ')}`}
          source={`attributes.${key}`}
        />
      ))}
    </SimpleForm>
  );
};

export const students = {
  list: StudentList,
  show: StudentShow,
  create: StudentCreate,
  edit: StudentEdit,
};
